export class LatestRequest {
  private generation = 0;

  run<T>(ask: () => Promise<T>, apply: (value: T) => void, fail?: () => void): Promise<void> {
    const mine = ++this.generation;
    return ask().then(
      (value) => {
        if (mine === this.generation) apply(value);
      },
      () => {
        if (mine === this.generation) fail?.();
      },
    );
  }

  invalidate(): void {
    this.generation += 1;
  }
}

type Scheduler = (task: () => void) => () => void;

const nextTask: Scheduler = (task) => {
  const timer = setTimeout(task, 0);
  return () => clearTimeout(timer);
};

export function coalesced(
  refresh: () => Promise<unknown> | unknown,
  schedule: Scheduler = nextTask,
): { notice: () => void; cancel: () => void } {
  let cancelScheduled: (() => void) | null = null;
  let running = false;
  let dirty = false;
  let cancelled = false;

  const enqueue = () => {
    if (cancelled || cancelScheduled !== null) return;
    cancelScheduled = schedule(() => {
      cancelScheduled = null;
      if (cancelled) return;
      running = true;
      void Promise.resolve()
        .then(refresh)
        .catch(() => undefined)
        .then(() => {
          running = false;
          if (cancelled || !dirty) return;
          dirty = false;
          enqueue();
        });
    });
  };

  return {
    notice() {
      if (cancelled) return;
      if (running) {
        dirty = true;
        return;
      }
      enqueue();
    },
    cancel() {
      cancelled = true;
      dirty = false;
      cancelScheduled?.();
      cancelScheduled = null;
    },
  };
}
